// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import clsx from 'clsx';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
    ITextFieldStyles,
    Spinner,
    SpinnerSize,
    TextField,
} from 'office-ui-fabric-react';

import { validateOrderNumber } from './../../helpers/validation';
import { logPageAction } from './../../lib/ApplicationInsights';
import { resetStoreOnSearch } from './../../store/action';
import { actionStatusReset, LoadingState } from './../../store/actionStatus';
import {
    getServiceRequestData,
    GET_SERVICE_REQUEST_DATA
} from './../../store/deviceInfo/claimOrder';
import { setHiddenAnnouncement } from './../../store/hiddenAnnouncement';

import Styles from './serviceRequestAndDeviceLookup.module.scss';

export interface ServiceRequestInputPropType {
    onError?: (errorMessage: string) => void;
}

const textFieldStyles: Partial<ITextFieldStyles> = {
    root: {
        marginLeft: 22,
        width: 160,
    },
};

export default function ServiceRequestInput(props: ServiceRequestInputPropType): JSX.Element {
    const dispatch = useDispatch();

    const [serviceRequestNumber, setServiceRequestNumber] = React.useState('');

    const orderNumberLoadingState = useSelector((state: any) => state.actionStatus[GET_SERVICE_REQUEST_DATA]) || {};

    const isDisabled = orderNumberLoadingState.status === LoadingState.started;
    const loadingComplete = orderNumberLoadingState.status === LoadingState.success || orderNumberLoadingState.status === LoadingState.rejected;

    const buttonDisabled = isDisabled || !serviceRequestNumber;

    const buttonClassName = clsx(
        Styles.button,
        Styles.commonFocus,
        { [Styles.disabled]: buttonDisabled }
    );

    const { t } = useTranslation();

    function onOrderNumberChange(event: any) {
        dispatch(actionStatusReset({actionName: GET_SERVICE_REQUEST_DATA}));
        setServiceRequestNumber(event.target.value && event.target.value.trim());
        if (props.onError) {
            props.onError('');
        }
    }

    function onClick(event: any) {
        logPageAction('Continue to order details page: Select button clicked');
        
        event.preventDefault();
        dispatch(actionStatusReset({actionName: GET_SERVICE_REQUEST_DATA}));
        if (props.onError) {
            props.onError('');
        }

        if (!validate()) {
            if (props.onError) {
                props.onError(t('please_provide_valid_order_number'));
            }
            return;
        }

        dispatch(resetStoreOnSearch());
        dispatch(getServiceRequestData(serviceRequestNumber));
    }

    function validate(): boolean {
        if (!serviceRequestNumber || !validateOrderNumber(serviceRequestNumber)) {
            return false;
        }
        return true;
    }

    if (isDisabled) {
        dispatch(setHiddenAnnouncement({
            message: t('loading'),
        }));
    } else if (loadingComplete) {
        // Only say loading complete on success/failure, else React will go in infinite loop during change user tier
        dispatch(setHiddenAnnouncement({
            message: t('loading_complete'),
        }));
    }

    return (
        <form className={Styles.inputsSection}>
            <TextField
                ariaLabel={t('order_number')}
                autoComplete="on"
                disabled={isDisabled}
                label={t('order_number')}
                onChange={onOrderNumberChange}
                styles={textFieldStyles}
            />
            <button
                aria-label={t('select')}
                className={buttonClassName}
                disabled={buttonDisabled}
                type="submit"
                onClick={onClick}
            >
                {t('select')}
            </button>
            {isDisabled && <Spinner
                className={Styles.spinnerStyle}
                size={SpinnerSize.medium}
            />}
        </form>
    );
}
